import React from "react";
import {
  UseFormRegister,
  FieldErrors,
  Controller,
  Control,
  useWatch,
} from "react-hook-form";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { CampaignType } from "@/@types/gamification";
import { todayIsoDate } from "@/utils/date-string";
import { CampaignTypeSelector } from "../../components/campaign-type-selector";
import { CustomerFacingDescriptionField } from "../../components/customer-facing-description-field";
import { CustomerFacingNameField } from "../../components/customer-facing-name-field";
import {
  CAMPAIGN_TYPE_PREFIX,
  PRO015_DESCRIPTION_MAX,
  maxCampaignNameLength,
} from "../../constants/campaign-name";
import { type CreateGamificationFormData } from "../../validations/gamification.validation";

interface BasicInfoCardProps {
  register: UseFormRegister<CreateGamificationFormData>;
  control: Control<CreateGamificationFormData>;
  errors: FieldErrors<CreateGamificationFormData>;
  campaignNameLength: number;

  onCampaignTypeChange?: (type: CreateGamificationFormData["campaignType"]) => void;
}

export const BasicInfoCard: React.FC<BasicInfoCardProps> = ({
  register,
  control,
  errors,
  campaignNameLength,
  onCampaignTypeChange,
}) => {

  const campaignType =
    useWatch({ control, name: "campaignType" }) ?? CampaignType.ROULETTE;
  const nameLimit = maxCampaignNameLength(campaignType);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle>Informações Básicas</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <Controller
          control={control}
          name="campaignType"
          defaultValue={CampaignType.ROULETTE}
          render={({ field }) => (
            <CampaignTypeSelector
              value={field.value ?? CampaignType.ROULETTE}
              onChange={(type) => {
                field.onChange(type);
                onCampaignTypeChange?.(type);
              }}
            />
          )}
        />
        {errors.campaignType && (
          <p className="text-sm text-red-500 -mt-3">
            {errors.campaignType.message}
          </p>
        )}

        <div>
          <div className="flex justify-between items-center">
            <Label htmlFor="campaignName">Nome da Campanha *</Label>
            <span
              className={`text-xs ${campaignNameLength > nameLimit ? "text-red-500" : "text-gray-500"}`}
            >
              {campaignNameLength}/{nameLimit}
            </span>
          </div>
          <Input
            id="campaignName"
            placeholder="Ex: Black Friday 2024"
            maxLength={nameLimit}
            {...register("campaignName")}
          />
          <p className="text-xs text-gray-500 mt-1">
            O sistema grava "{CAMPAIGN_TYPE_PREFIX[campaignType]} + nome"
            (limite total de {PRO015_DESCRIPTION_MAX} caracteres).
          </p>
          {errors.campaignName && (
            <p className="text-sm text-red-500 mt-1">
              {errors.campaignName.message}
            </p>
          )}
        </div>

        <CustomerFacingNameField
          control={control}
          register={register}
          errorMessage={
            errors.mechanicConfig?.presentation?.customerFacingName?.message
          }
        />

        <CustomerFacingDescriptionField
          control={control}
          register={register}
          errorMessage={
            errors.mechanicConfig?.presentation?.customerFacingDescription
              ?.message
          }
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label htmlFor="startDate">Data de Início *</Label>
            <Input
              id="startDate"
              type="date"
              min={todayIsoDate()}
              {...register("startDate")}
            />
            {errors.startDate && (
              <p className="text-sm text-red-500 mt-1">
                {errors.startDate.message}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="endDate">Data de Fim *</Label>
            <Input id="endDate" type="date" {...register("endDate")} />
            {errors.endDate && (
              <p className="text-sm text-red-500 mt-1">
                {errors.endDate.message}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
